import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, AlertTriangle, Lightbulb, Target, Calendar } from 'lucide-react';

const AiAnalysisDisplay = ({ analysis }) => {
  // Parse the markdown analysis into sections
  const parseAnalysis = (text) => {
    const sections = {};
    const lines = text.split('\n');
    let currentSection = null;
    let currentSubsection = null;
    let content = [];

    lines.forEach(line => {
      // Main sections (##)
      if (line.startsWith('## ')) {
        if (currentSection) {
          if (currentSubsection) {
            sections[currentSection].subsections[currentSubsection] = content.join('\n');
          } else {
            sections[currentSection].content = content.join('\n');
          }
        }
        currentSection = line.replace('## ', '').replace(/[📊🔍💡🎯📈⚠️]/g, '').trim();
        currentSubsection = null;
        content = [];
        sections[currentSection] = { subsections: {}, content: '' };
      }
      // Subsections (###)
      else if (line.startsWith('### ')) {
        if (currentSubsection && currentSection) {
          sections[currentSection].subsections[currentSubsection] = content.join('\n');
        } else if (currentSection && content.length > 0) {
          sections[currentSection].content = content.join('\n');
        }
        currentSubsection = line.replace('### ', '').trim();
        content = [];
      }
      // Regular content
      else if (currentSection) {
        content.push(line);
      }
    });

    // Save last section
    if (currentSection) {
      if (currentSubsection) {
        sections[currentSection].subsections[currentSubsection] = content.join('\n');
      } else {
        sections[currentSection].content = content.join('\n');
      }
    }

    return sections;
  };

  const sections = parseAnalysis(analysis);

  // Clean text from emojis and markdown
  const cleanText = (text) => {
    if (!text) return '';
    return text
      .replace(/[📊🔍💡🎯📈⚠️🚨🌡️🌿🍂♻️🔋🤖]/g, '')
      .replace(/\*\*/g, '')
      .replace(/\*/g, '')
      .trim();
  };

  // Render markdown-style content with proper formatting
  const renderContent = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    return lines.map((line, idx) => {
      // Bold list items (- **text**: description)
      if (line.trim().startsWith('- **')) {
        const match = line.match(/^-\s*\*\*(.+?)\*\*:?\s*(.*)/);
        if (match) {
          return (
            <li key={idx} className="text-sm text-[#3C4649] flex items-start gap-2 ml-2 mb-1.5">
              <span className="text-[#397DFF] mt-0.5">•</span>
              <span><strong className="font-semibold text-[#191D1E]">{match[1]}</strong>{match[2] ? `: ${match[2]}` : ''}</span>
            </li>
          );
        }
      }
      // Regular list items
      if (line.trim().startsWith('- ')) {
        return (
          <li key={idx} className="text-sm text-[#3C4649] flex items-start gap-2 ml-2 mb-1">
            <span className="text-[#397DFF] mt-0.5">•</span>
            <span>{cleanText(line.replace(/^-\s*/, ''))}</span>
          </li>
        );
      }
      // Numbered list items
      if (/^\d+\.\s/.test(line.trim())) {
        const num = line.match(/^(\d+)\./)[1];
        const content = line.replace(/^\d+\.\s*/, '');
        const boldMatch = content.match(/^\*\*(.+?)\*\*:?\s*(.*)/);
        return (
          <li key={idx} className="text-sm text-[#3C4649] flex items-start gap-2 ml-2 mb-1.5">
            <span className="text-[#397DFF] font-semibold min-w-[18px]">{num}.</span>
            {boldMatch ? (
              <span><strong className="font-semibold text-[#191D1E]">{boldMatch[1]}</strong>{boldMatch[2] ? `: ${boldMatch[2]}` : ''}</span>
            ) : (
              <span>{cleanText(content)}</span>
            )}
          </li>
        );
      }
      // Empty lines
      if (!line.trim()) {
        return null;
      }
      // Regular paragraphs
      return <p key={idx} className="text-sm text-[#3C4649] mb-2 leading-relaxed">{cleanText(line)}</p>;
    }).filter(Boolean);
  };

  // Get icon for section
  const getSectionIcon = (title) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('vue') || lowerTitle.includes('synthèse') || lowerTitle.includes('global')) return TrendingUp;
    if (lowerTitle.includes('tendance') || lowerTitle.includes('mensuel')) return Calendar;
    if (lowerTitle.includes('évolution') || lowerTitle.includes('année')) return TrendingUp;
    if (lowerTitle.includes('puissance') || lowerTitle.includes('pic')) return AlertTriangle;
    if (lowerTitle.includes('attention') || lowerTitle.includes('alerte')) return AlertTriangle;
    if (lowerTitle.includes('recommandation') || lowerTitle.includes('action')) return Lightbulb;
    if (lowerTitle.includes('indicateur') || lowerTitle.includes('kpi') || lowerTitle.includes('clé')) return Target;
    if (lowerTitle.includes('prévision') || lowerTitle.includes('projection')) return TrendingDown;
    return TrendingUp;
  };

  // Get card style for section
  const getCardStyle = (title) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('vue') || lowerTitle.includes('synthèse')) 
      return 'border-primary/20 bg-gradient-to-br from-primary/5 to-transparent';
    if (lowerTitle.includes('recommandation') || lowerTitle.includes('action')) 
      return 'border-blue-200 bg-blue-50/50';
    if (lowerTitle.includes('indicateur') || lowerTitle.includes('kpi')) 
      return 'border-green-200 bg-green-50/50';
    if (lowerTitle.includes('attention') || lowerTitle.includes('alerte')) 
      return 'border-amber-200 bg-amber-50/50';
    return 'hover:shadow-md transition-shadow';
  };

  // Render ALL sections dynamically
  return (
    <div className="space-y-6">
      {Object.entries(sections).map(([sectionTitle, sectionData]) => {
        const Icon = getSectionIcon(sectionTitle);
        const cardStyle = getCardStyle(sectionTitle);
        const hasSubsections = Object.keys(sectionData.subsections).length > 0;
        const hasContent = sectionData.content && sectionData.content.trim().length > 0;

        return (
          <Card key={sectionTitle} className={cardStyle}>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Icon className="h-5 w-5 text-primary" />
                <CardTitle className="text-xl">{cleanText(sectionTitle)}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Main section content */}
              {hasContent && (
                <div className="text-base leading-relaxed text-muted-foreground">
                  {renderContent(sectionData.content)}
                </div>
              )}
              
              {/* Subsections */}
              {hasSubsections && (
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  {Object.entries(sectionData.subsections).map(([subTitle, subContent]) => {
                    if (!subContent || !subContent.trim()) return null;
                    const SubIcon = getSectionIcon(subTitle);
                    
                    return (
                      <div key={subTitle} className="bg-white/50 rounded-lg p-4 border">
                        <div className="flex items-center gap-2 mb-3">
                          <SubIcon className="h-4 w-4 text-primary" />
                          <h4 className="font-semibold text-sm text-[#191D1E]">{cleanText(subTitle)}</h4>
                        </div>
                        <div className="space-y-1">
                          {renderContent(subContent)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Footer note */}
      <div className="text-center text-xs text-muted-foreground italic pt-4 border-t">
        Analyse générée par Energy Manager
      </div>
    </div>
  );
};

export default AiAnalysisDisplay;
